import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Badge, Button, Card, Col, Form, Modal, Row, Spinner, Table } from 'react-bootstrap';
import { format } from 'date-fns';
import { toast } from 'react-toastify';
import { aiMonitoringAPI } from '../../services';
import PaginationComponent from '../../components/PaginationComponent';
import useFilterParams from '../../hooks/useFilterParams';
import InvalidParamsNotice from '../../components/InvalidParamsNotice';

/**
 * #162225 — Tab cảnh báo đối soát camera ↔ bill (luồng cũ theo bàn). #181800: tách khỏi trang để trang
 * Đối soát có 2 tab (Lịch sử nhận diện · Cảnh báo không order).
 *
 * Backend (`src/modules/ai-monitoring/`, có từ 25/03/2026) đã có sẵn 3 endpoint nhưng admin
 * CHƯA gọi tới đâu cả — đây là màn đầu tiên dùng chúng. Cảnh báo `TABLE_NO_ORDER` = camera
 * thấy người ở bàn nhưng không có TableSession nào ⇒ nghi nhận order miệng, không vào bill.
 *
 * Chủ ý thiết kế (theo user story #160852 mục 3, AC5/AC6):
 * - Đây là CẢNH BÁO MỀM cho quản lý tự ra bàn kiểm, KHÔNG phải quy trình duyệt/phạt. Vì vậy
 *   màn này chỉ có đúng một hành động "Đã xử lý" + ghi chú, không có nút phạt điểm nhân viên
 *   (phần nối `Point` đang chờ sếp chốt, phiếu td16202822809 — cố ý không code).
 * - Danh sách nhân viên đang trực (`shift_employees`) được chụp tại thời điểm cảnh báo và hiện
 *   ngay trên dòng, để quản lý loại trừ bằng mắt tình huống "nhân viên ngồi nghỉ/ăn ca" —
 *   camera đếm người không phân biệt được NV với khách (user story mục 2, tình huống #4).
 */

const RESOLVED_OPTIONS = [
  { value: 'false', label: 'Chưa xử lý' },
  { value: 'true', label: 'Đã xử lý' },
  { value: 'ALL', label: 'Tất cả' },
];

const ALERT_TYPE_OPTIONS = [
  { value: 'ALL', label: 'Tất cả loại' },
  { value: 'TABLE_NO_ORDER', label: 'Bàn có người, không có order' },
  { value: 'TABLE_DIRTY', label: 'Bàn bẩn chưa dọn' },
  { value: 'PARKING_VIOLATION', label: 'Vi phạm bãi xe' },
];

const ALERT_TYPE_LABEL = ALERT_TYPE_OPTIONS.reduce((acc, o) => {
  if (o.value !== 'ALL') acc[o.value] = o.label;
  return acc;
}, {});

const SEVERITY_BADGE = { CRITICAL: 'danger', WARNING: 'warning', INFO: 'secondary' };
const SEVERITY_LABEL = { CRITICAL: 'Nghiêm trọng', WARNING: 'Cảnh báo', INFO: 'Thông tin' };

const formatDateTime = (value, pattern = 'HH:mm dd/MM/yyyy') => {
  if (!value) return '-';
  try {
    return format(new Date(value), pattern);
  } catch {
    return value;
  }
};

const parseListResponse = (response) => {
  const payload = response?.data ?? response;
  if (Array.isArray(payload)) {
    return { items: payload, total: payload.length, page: 1, limit: 20 };
  }
  const items = Array.isArray(payload?.data) ? payload.data : [];
  return {
    items,
    total: payload?.total ?? items.length,
    page: payload?.page ?? 1,
    limit: payload?.limit ?? 20,
  };
};

const isViewableImage = (path) => typeof path === 'string' && /^https?:\/\//i.test(path);

// `ALL` trên URL = bỏ lọc. Không dùng chuỗi rỗng: `getParam` coi param rỗng như không có
// nên F5 sẽ rơi về mặc định (đúng bài học #210029 của màn báo cáo sự cố).
const ALL = 'ALL';
const ALERT_FILTER_SPEC = {
  resolved: {
    default: 'false',
    valid: (v) => ['true', 'false', ALL].includes(v.toLowerCase()) || v.toUpperCase() === ALL,
    parse: (v) => (v.toUpperCase() === ALL ? ALL : v.toLowerCase()),
  },
  alert_type: {
    default: ALL,
    parse: (v) => v.toUpperCase(),
    valid: (v) => ALERT_TYPE_OPTIONS.some((o) => o.value === v.toUpperCase()),
  },
  from: { default: '' },
  to: { default: '' },
};

const AiMonitoringAlertsTab = ({ branchId }) => {

  const [urlFilters, setUrlFilters, , { ignored, dismissIgnored }] =
    useFilterParams(ALERT_FILTER_SPEC);
  const { resolved: resolvedFilter, alert_type: alertTypeFilter, from: fromFilter, to: toFilter } =
    urlFilters;

  const [alerts, setAlerts] = useState([]);
  const [meta, setMeta] = useState({ total: 0, page: 1, limit: 20 });
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);

  const [resolveModal, setResolveModal] = useState({ show: false, alert: null });
  const [resolveNote, setResolveNote] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const [comparison, setComparison] = useState(null);
  const [comparisonLoading, setComparisonLoading] = useState(false);

  const fetchAlerts = useCallback(async () => {
    try {
      setLoading(true);
      setLoadError(null);
      const params = {
        branch_id: branchId || undefined,
        alert_type: alertTypeFilter === ALL ? undefined : alertTypeFilter,
        resolved: resolvedFilter === ALL ? undefined : resolvedFilter,
        // API nhận ISO date-string; ô <input type="date"> cho "yyyy-MM-dd" nên phải nới ra
        // đủ cả ngày, nếu không lọc "đến ngày X" sẽ cắt mất mọi cảnh báo trong chính ngày X.
        from: fromFilter ? new Date(`${fromFilter}T00:00:00`).toISOString() : undefined,
        to: toFilter ? new Date(`${toFilter}T23:59:59`).toISOString() : undefined,
        page: meta.page,
        limit: meta.limit,
      };
      const response = await aiMonitoringAPI.getAlerts(params);
      const { items, total, page, limit } = parseListResponse(response);
      setAlerts(items);
      setMeta({ total, page, limit });
    } catch (error) {
      console.error('Cannot load monitoring alerts', error);
      const message = error?.message || 'Không tải được danh sách cảnh báo';
      setLoadError(message);
      setAlerts([]);
    } finally {
      setLoading(false);
    }
  }, [branchId, alertTypeFilter, resolvedFilter, fromFilter, toFilter, meta.page, meta.limit]);

  useEffect(() => {
    fetchAlerts();
  }, [fetchAlerts]);

  const handleFilterChange = (patch) => {
    setUrlFilters(patch);
    setMeta((prev) => ({ ...prev, page: 1 }));
  };

  const handleResolve = async () => {
    const alert = resolveModal.alert;
    if (!alert?.id) return;
    try {
      setActionLoading(true);
      await aiMonitoringAPI.resolveAlert(alert.id, resolveNote.trim());
      toast.success('Đã đánh dấu cảnh báo là đã xử lý');
      setResolveModal({ show: false, alert: null });
      setResolveNote('');
      fetchAlerts();
    } catch (error) {
      toast.error(error?.message || 'Không lưu được, thử lại');
    } finally {
      setActionLoading(false);
    }
  };

  const loadComparison = async () => {
    if (!branchId) {
      toast.warn('Chọn chi nhánh trước khi đối chiếu trực tiếp');
      return;
    }
    try {
      setComparisonLoading(true);
      const res = await aiMonitoringAPI.getComparison(branchId);
      setComparison(res?.data ?? res);
    } catch (error) {
      toast.error(error?.message || 'Không lấy được dữ liệu đối chiếu');
      setComparison(null);
    } finally {
      setComparisonLoading(false);
    }
  };

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil((meta.total || 0) / (meta.limit || 20))),
    [meta.total, meta.limit],
  );

  const renderTables = (alert) => {
    const tables = alert?.details?.unmatched_tables ?? [];
    if (!tables.length) return <span className="text-muted">-</span>;
    return (
      <div className="d-flex flex-wrap gap-1">
        {tables.map((t) => (
          <Badge bg="danger" key={t}>
            Bàn {t}
          </Badge>
        ))}
      </div>
    );
  };

  const renderEmployees = (alert) => {
    const employees = Array.isArray(alert?.shift_employees) ? alert.shift_employees : [];
    if (!employees.length) return <span className="text-muted small">Không có NV trực</span>;
    return (
      <div className="small">
        {employees.map((e, i) => (
          <div key={e?.id ?? i}>
            {e?.name ?? e?.id ?? '-'}
            {e?.is_cashier ? <span className="text-muted"> (thu ngân)</span> : null}
          </div>
        ))}
      </div>
    );
  };

  const renderSnapshots = (alert) => {
    const paths = Array.isArray(alert?.snapshot_paths) ? alert.snapshot_paths : [];
    if (!paths.length) return <span className="text-muted small">Không có ảnh</span>;
    return (
      <div className="d-flex flex-wrap gap-2">
        {paths.map((p, i) =>
          isViewableImage(p) ? (
            <a key={`${p}-${i}`} href={p} target="_blank" rel="noreferrer">
              <img
                src={p}
                alt={`Ảnh camera ${i + 1}`}
                style={{ width: 84, height: 60, objectFit: 'cover', borderRadius: 4 }}
              />
            </a>
          ) : (
            // Dịch vụ camera có thể trả đường dẫn nội bộ (chưa phải URL công khai) — hiện
            // nguyên văn để quản lý còn tra được file, thay vì vẽ ra ô ảnh vỡ.
            <code key={`${p}-${i}`} className="small text-muted">
              {p}
            </code>
          ),
        )}
      </div>
    );
  };

  return (
    <>
      <div className="cambill-toolbar">
        <p className="cambill-hint mb-0">
          Cảnh báo cũ theo bàn: camera thấy người nhưng bàn không có order/phiên. Cảnh báo mềm để quản lý ra
          kiểm — hệ thống không tự phạt nhân viên.
        </p>
        <div className="d-flex gap-2 flex-shrink-0">
          <Button size="sm" variant="outline-secondary" onClick={loadComparison} disabled={comparisonLoading}>
            {comparisonLoading ? 'Đang lấy...' : 'Đối chiếu ngay'}
          </Button>
          <Button size="sm" variant="outline-primary" onClick={fetchAlerts} disabled={loading}>
            Làm mới
          </Button>
        </div>
      </div>

        <InvalidParamsNotice ignored={ignored} onDismiss={dismissIgnored} />

        {comparison && (
          <Card className="mb-4">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <strong>Đối chiếu trực tiếp (không chờ cảnh báo)</strong>
              <Button size="sm" variant="link" onClick={() => setComparison(null)}>
                Đóng
              </Button>
            </Card.Header>
            <Card.Body className="small">
              <div>
                Camera thấy người: <strong>{(comparison.camera_occupied_tables ?? []).join(', ') || '—'}</strong>
              </div>
              <div>
                Bàn có session ACTIVE: <strong>{(comparison.session_active_tables ?? []).join(', ') || '—'}</strong>
              </div>
              <div>
                Bàn vừa kết thúc (còn trong {comparison.recently_ended_grace_minutes ?? 0} phút đệm):{' '}
                <strong>{(comparison.recently_ended_tables ?? []).join(', ') || '—'}</strong>
              </div>
              <div className="text-danger">
                Có người mà KHÔNG có session:{' '}
                <strong>{(comparison.tables_with_people_no_session ?? []).join(', ') || '—'}</strong>
              </div>
              <div className="text-muted">
                Có session mà camera không thấy người:{' '}
                {(comparison.tables_with_session_no_people ?? []).join(', ') || '—'}
              </div>
            </Card.Body>
          </Card>
        )}

        <Card className="mb-4">
          <Card.Header className="bg-white py-3">
            <Row className="g-3 align-items-end">
              <Col xs={12} sm={6} lg={3}>
                <Form.Label htmlFor="canh-bao-trang-thai">Trạng thái</Form.Label>
                <Form.Select
                  id="canh-bao-trang-thai"
                  aria-label="Trạng thái xử lý"
                  value={resolvedFilter}
                  onChange={(e) => handleFilterChange({ resolved: e.target.value })}
                >
                  {RESOLVED_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col xs={12} sm={6} lg={3}>
                <Form.Label htmlFor="canh-bao-loai">Loại cảnh báo</Form.Label>
                <Form.Select
                  id="canh-bao-loai"
                  aria-label="Loại cảnh báo"
                  value={alertTypeFilter}
                  onChange={(e) => handleFilterChange({ alert_type: e.target.value })}
                >
                  {ALERT_TYPE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col xs={6} lg={2}>
                <Form.Label htmlFor="canh-bao-tu">Từ ngày</Form.Label>
                <Form.Control
                  id="canh-bao-tu"
                  type="date"
                  aria-label="Từ ngày"
                  value={fromFilter}
                  onChange={(e) => handleFilterChange({ from: e.target.value })}
                />
              </Col>
              <Col xs={6} lg={2}>
                <Form.Label htmlFor="canh-bao-den">Đến ngày</Form.Label>
                <Form.Control
                  id="canh-bao-den"
                  type="date"
                  aria-label="Đến ngày"
                  value={toFilter}
                  onChange={(e) => handleFilterChange({ to: e.target.value })}
                />
              </Col>
            </Row>
          </Card.Header>
          <Card.Body className="p-0">
            {loading ? (
              <div className="text-center py-5">
                <Spinner animation="border" size="sm" /> Đang tải...
              </div>
            ) : loadError ? (
              <div className="text-center text-danger py-5">
                Không tải được danh sách cảnh báo: {loadError}
              </div>
            ) : alerts.length === 0 ? (
              <div className="text-center text-muted py-5">Không có cảnh báo nào.</div>
            ) : (
              <div className="table-responsive">
                <Table striped hover className="mb-0 align-middle">
                  <thead>
                    <tr>
                      <th>Thời gian</th>
                      <th>Chi nhánh</th>
                      <th>Loại / mức</th>
                      <th>Bàn nghi ngờ</th>
                      <th>NV đang trực</th>
                      <th>Ảnh camera</th>
                      <th>Trạng thái</th>
                      <th className="text-end">Thao tác</th>
                    </tr>
                  </thead>
                  <tbody>
                    {alerts.map((a) => (
                      <tr key={a.id} id={`monitoring-alert-${a.id}`}>
                        <td className="text-nowrap">{formatDateTime(a.created_at)}</td>
                        <td>{a.branch?.name ?? a.branch_id ?? '-'}</td>
                        <td>
                          <div>{ALERT_TYPE_LABEL[a.alert_type] ?? a.alert_type}</div>
                          <Badge bg={SEVERITY_BADGE[a.severity] || 'secondary'}>
                            {SEVERITY_LABEL[a.severity] ?? a.severity}
                          </Badge>
                        </td>
                        <td>{renderTables(a)}</td>
                        <td>{renderEmployees(a)}</td>
                        <td>{renderSnapshots(a)}</td>
                        <td>
                          {a.resolved ? (
                            <>
                              <Badge bg="success">Đã xử lý</Badge>
                              <div className="small text-muted">{formatDateTime(a.resolved_at)}</div>
                              {a.resolve_note && (
                                <div className="small text-muted">“{a.resolve_note}”</div>
                              )}
                            </>
                          ) : (
                            <Badge bg="warning" text="dark">
                              Chưa xử lý
                            </Badge>
                          )}
                        </td>
                        <td className="text-end">
                          {!a.resolved && (
                            <Button
                              size="sm"
                              variant="outline-success"
                              onClick={() => {
                                setResolveNote('');
                                setResolveModal({ show: true, alert: a });
                              }}
                            >
                              Đã xử lý
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </Card.Body>
          {totalPages > 1 && (
            <Card.Footer className="bg-white">
              <PaginationComponent
                totalItems={meta.total}
                itemsPerPage={meta.limit}
                currentPage={meta.page}
                onPageChange={(page) => setMeta((prev) => ({ ...prev, page }))}
                loading={loading}
              />
            </Card.Footer>
          )}
        </Card>

      <Modal
        show={resolveModal.show}
        onHide={() => setResolveModal({ show: false, alert: null })}
      >
        <Modal.Header closeButton>
          <Modal.Title>Đánh dấu đã xử lý</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {resolveModal.alert && (
            <>
              <p className="mb-1">
                <strong>{resolveModal.alert.summary}</strong>
              </p>
              <p className="text-muted small mb-3">
                {resolveModal.alert.branch?.name ?? resolveModal.alert.branch_id} ·{' '}
                {formatDateTime(resolveModal.alert.created_at)}
              </p>
              <Form.Group>
                <Form.Label>Ghi chú xử lý (tối đa 500 ký tự)</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  maxLength={500}
                  placeholder="VD: ra bàn kiểm — khách vừa dọn đi, camera bắt nhầm nhân viên lau bàn"
                  value={resolveNote}
                  onChange={(e) => setResolveNote(e.target.value)}
                />
              </Form.Group>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setResolveModal({ show: false, alert: null })}>
            Hủy
          </Button>
          <Button variant="success" onClick={handleResolve} disabled={actionLoading}>
            {actionLoading ? 'Đang lưu...' : 'Xác nhận đã xử lý'}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default AiMonitoringAlertsTab;
